import { createClient } from "@supabase/supabase-js";

// Reads from Vite's import.meta.env, populated from a local .env file
// (copy .env.example -> .env and fill in your Supabase project's URL +
// publishable key). See supabase/schema.sql for the table + RLS policies
// this expects to exist.
//
// Use the new "Publishable key" from Project Settings -> API Keys, not the
// legacy JWT-format "anon" key - Supabase is phasing the legacy keys out,
// and the publishable key is a drop-in replacement here (same public,
// client-safe role, just a new key format - createClient() takes it the
// same way).
const url = import.meta.env.VITE_SUPABASE_URL;
const publishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

const TABLE = "leaderboard";

export const supabaseConfigured = Boolean(url && publishableKey);

export const supabase = supabaseConfigured ? createClient(url, publishableKey) : null;

if (!supabaseConfigured) {
  console.warn(
    "[supabase] VITE_SUPABASE_URL / VITE_SUPABASE_PUBLISHABLE_KEY aren't set - the leaderboard is running without a backend. " +
      "Copy .env.example to .env, fill in your Supabase project's URL + publishable key, and restart the dev server to connect it."
  );
}

/** Top N fastest finds, fastest first. Returns [] if Supabase isn't configured or the query fails. */
export async function fetchTopScores(limit = 5) {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from(TABLE)
    .select("handle, seconds, found")
    .eq("found", true)
    .order("seconds", { ascending: true })
    .limit(limit);
  if (error) {
    console.warn("[supabase] failed to load the leaderboard - check supabase/schema.sql is applied and RLS allows public reads.", error);
    return [];
  }
  return data ?? [];
}

/**
 * Starts a server-tracked round session (see start_leaderboard_round() in
 * supabase/schema.sql) - the database stamps the start time itself, so the
 * elapsed time submit_leaderboard_score() later computes from it can't be
 * forged by the client. Returns null (rather than throwing) if Supabase
 * isn't configured or the call fails - callers just treat that as "this
 * run won't be submittable," the same as any other run started before
 * Supabase was reachable.
 */
export async function startRound() {
  if (!supabase) return null;
  const { data, error } = await supabase.rpc("start_leaderboard_round");
  if (error) {
    console.warn("[supabase] failed to start a round session - this run won't be submittable.", error);
    return null;
  }
  return data;
}

/**
 * Stamps a round session's end time server-side (see end_leaderboard_round()
 * in supabase/schema.sql) - called the instant a round actually ends
 * (hider caught or timer run out), *before* the leaderboard entry screen
 * even shows. Returns the exact elapsed seconds the database just computed
 * from that timestamp (or null if the session was invalid/expired/already
 * ended) - main.js uses this as the displayed "Your time" instead of the
 * client's own locally-ticked timer, so the number shown on screen and the
 * number submit_leaderboard_score() later writes to the leaderboard (which
 * recomputes from these same two stored timestamps) are always identical,
 * not just close. Falls back to null (rather than throwing) if Supabase
 * isn't configured or the call fails - callers fall back to the client's
 * own timer for display in that case.
 */
export async function endRound(sessionId) {
  if (!supabase || !sessionId) return null;
  const { data, error } = await supabase.rpc("end_leaderboard_round", { p_session_id: sessionId });
  if (error) {
    console.warn("[supabase] failed to stamp round end - falling back to the client's own timer for display.", error);
    return null;
  }
  return data;
}

/**
 * Upserts a run via the submit_leaderboard_score() function (see
 * supabase/schema.sql) - one row per handle, keeping only the fastest time.
 * The elapsed time is computed server-side from the sessionId's start
 * time, not supplied here - there's no "seconds" parameter to forge.
 * Throws if Supabase isn't configured, there's no session (startRound()
 * failed or was never called), or the call fails - callers show that as a
 * submit error. Returns { bestSeconds, isNewBest } - isNewBest comes
 * straight from the database rather than being inferred by comparing
 * bestSeconds to a locally-tracked time, since the actual elapsed seconds
 * is now computed server-side and won't exactly match whatever the client
 * displayed during the round.
 */
export async function submitScore({ handle, found, sessionId }) {
  if (!supabase) throw new Error("Supabase isn't configured - see .env.example.");
  if (!sessionId) throw new Error("No active round session - can't submit a score for this run.");
  const { data, error } = await supabase.rpc("submit_leaderboard_score", {
    p_session_id: sessionId,
    p_handle: handle,
    p_found: found,
  });
  if (error) throw error;
  const [row] = data;
  return { bestSeconds: row.best_seconds, isNewBest: row.is_new_best };
}
